import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';

import { CustomerCategory } from '@/types';
import { useUpdateCustomerCategoryMutation } from '@/services';
import { messages } from '@/utils/helpers';
import { SearchColors } from '@/pages/Colors/components';

export interface UpdateCustomerCategoryProps extends DialogProps {
  customerCategory: CustomerCategory;
  onClose(): void;
}

const UpdateCustomerCategory: React.FC<UpdateCustomerCategoryProps> = ({ customerCategory, onClose, ...props }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [updateCustomerCategory, updateCustomerCategoryResponse] = useUpdateCustomerCategoryMutation();

  const { values, errors, touched, setFieldValue, handleSubmit, handleChange, resetForm } = useFormik<CustomerCategory>(
    {
      initialValues: customerCategory ?? {
        name: '',
        color: null,
      },
      enableReinitialize: true,
      validationSchema: yup.object().shape({
        name: yup.string().required(messages.form.REQUIRED),
        color: yup.object().nullable().required(messages.form.SELECT_FROM_LIST),
      }),
      async onSubmit(customerCategory) {
        try {
          await updateCustomerCategory(customerCategory).unwrap();

          handleClose();
        } catch (error) {
          enqueueSnackbar(error.data, { variant: 'error' });
        }
      },
    },
  );

  function handleClose() {
    onClose();
    resetForm();
  }

  return (
    <Dialog onClose={handleClose} fullWidth {...props}>
      <DialogTitle fontWeight="bold">Редактировать категорию клиента</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="name"
                label="Наименование"
                placeholder="Введите значение"
                value={values.name}
                onChange={handleChange}
                disabled={updateCustomerCategoryResponse.isLoading}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name ? errors.name : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <SearchColors
                value={values.color}
                onChange={(event, option) => setFieldValue('color', option)}
                getOptionLabel={(option) => option.name}
                disabled={updateCustomerCategoryResponse.isLoading}
                renderOption={(props, { name, hex }) => (
                  <li {...props}>
                    <Box sx={{ width: 32, height: 32, mr: 1, bgcolor: hex, borderRadius: '10em' }} />
                    {name}
                  </li>
                )}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    name="color"
                    label="Цвет"
                    placeholder="Выберите из списка"
                    error={Boolean(touched.color && errors.color)}
                    helperText={touched.color && errors.color}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            size="large"
            disabled={updateCustomerCategoryResponse.isLoading}
            onClick={handleClose}
          >
            Закрыть
          </Button>

          <Button type="submit" variant="contained" size="large" disabled={updateCustomerCategoryResponse.isLoading}>
            Редактировать
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateCustomerCategory;
